// SchedulerViewColumnHeader.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';

// CSS
import './SchedulerViewColumnHeader.css';

// Components
import SchedulerViewHeaderPhoto from './SchedulerViewHeaderPhoto';

/**
 * SchedulerViewColumnHeader
 *
 * Renders a scheduler resource column header with an optional color dot and cover photo.
 *
 * @param {string} backgroundColor - Background color for the header card.
 * @param {string} borderColor - Border color for the header card and cover photo.
 * @param {string} foregroundColor - Text color for the header title.
 * @param {object} resourceNode - Resource node represented by the scheduler column.
 *
 * @returns {ReactNode} The rendered column header.
 */
const SchedulerViewColumnHeader = ({
    resourceNode,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- HOOKS ----- //

    // Node Open Intent

    const openNode = useDefaultNodeOpen();

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);

    const resourceColor = typeof resourceNode?.color === 'string'
        ? resourceNode.color.trim()
        : '';

    // ----- HANDLERS ----- //

    /**
     * handleHeaderClick
     *
     * Opens the resource node using the app's default peek-first behavior.
     */

    const handleHeaderClick = () => {
        if (!resourceNode?.key) return;
        openNode(resourceNode);
    };

    /**
     * handleHeaderKeyDown
     *
     * Opens the resource node when the header receives Enter or Space.
     *
     * @param {object} event - Keyboard event from the header container.
     */
    const handleHeaderKeyDown = (event) => {
        if (event?.key !== 'Enter' && event?.key !== ' ') return;

        event.preventDefault();
        handleHeaderClick();
    };

    // ----- RENDER ----- //

    return (
        <div
          className="scheduler-view-column-header-container"
          style={{
                background: hovered ? theme.buttonHoverBackgroundColor : theme.panel,
                color: hovered ? theme.buttonHoverLabelColor : theme.foreground,
            }}
          role="button"
          tabIndex={0}
          onClick={handleHeaderClick}
          onKeyDown={handleHeaderKeyDown}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >

            {/* MAIN */}
            <div className="scheduler-view-column-header-main">
                {resourceColor && (
                    <span
                      className="scheduler-view-resource-dot scheduler-view-resource-dot-header"
                      style={{
                            backgroundColor: resourceColor,
                        }}
                    />
                )}

                <SchedulerViewHeaderPhoto
                  node={resourceNode}
                  size={22}
                  borderWidth={1}
                />

                {/* TITLE */}
                <div className="scheduler-view-column-header-title">{resourceNode?.title || 'Untitled'}</div>
            </div>
        </div>
    );
};

// Dev Tools
SchedulerViewColumnHeader.displayName = 'SchedulerViewColumnHeader';

export default SchedulerViewColumnHeader;
